import * as React from "react";
import { Link } from "react-router-dom";
import { Image as ImageIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Props {
  href?: string;
  title?: React.ReactNode | string;
  dateLabel?: string;
  thumbnail?: string;
  floatingIcon?: React.ReactNode | string;
  className?: string;
  disabled?: boolean;
  extra?: React.ReactNode;
  dataSkeleton?: boolean;
  onClick?: () => void;
}

const LibraryCard: React.FC<Props> = ({
  href,
  title,
  dateLabel,
  thumbnail,
  floatingIcon,
  className,
  disabled = false,
  extra,
  dataSkeleton = false,
  onClick,
}: Props) => {
  const isLink = !!href && !disabled;
  const isClickable = (!!onClick || !!href) && !disabled;

  const card = (
    <Card
      className={cn(
        "relative bg-background rounded-lg border-border border shadow-none w-38 min-w-38 h-45 min-h-45 hover:shadow-lg hover:-translate-y-1 mx-0 my-2 overflow-hidden py-0 gap-0 ring-1 ring-foreground/10 group",
        isClickable && "cursor-pointer transition-all duration-300",
        className
      )}
      disabled={disabled}
      data-skeleton={dataSkeleton}
      onClick={(e: React.MouseEvent) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
        onClick?.();
      }}
    >
      {floatingIcon && (
        <div className="absolute m-0 top-2 left-2 z-10 bg-transparent">
          {typeof floatingIcon === "string" ? <img className="size-8" src={floatingIcon} /> : floatingIcon}
        </div>
      )}
      <div className="flex-1 w-full bg-primary-foreground flex items-center justify-center overflow-hidden shrink-0">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt="Library Card Thumbnail"
            className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
            style={{
              backgroundColor: "transparent",
            }}
          />
        ) : (
          <div className="flex items-center justify-center w-full text-muted-foreground/40 transition-transform duration-500 group-hover:scale-105">
            <ImageIcon className="size-10" strokeWidth={1.5} />
          </div>
        )}
      </div>

      <Card.Content className="p-2.5 flex flex-col gap-0.5 shrink-0">
        {title && (
          <div className="whitespace-nowrap text-ellipsis m-0 w-auto text-xs/4 font-semibold text-foreground overflow-x-clip">
            {title}
          </div>
        )}
        {dateLabel && <div className="text-muted-foreground text-[10px]/3">{dateLabel}</div>}
      </Card.Content>
      {extra && <Card.Footer className="bg-transparent border-t-0 p-2.5 pt-0 flex-none shrink-0">{extra}</Card.Footer>}
    </Card>
  );

  if (isLink) {
    return (
      <Link to={href} className="contents">
        {card}
      </Link>
    );
  }

  return card;
};

export default LibraryCard;
